import { Box } from '@mui/material';
import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import UpsellModal from 'components/Modals/UpsellModal';
import { ReleaseLimitDialog } from 'components/Modals/implementations/ReleaseLimitDialog';
import UpgradeDistributionModal from 'components/Modals/UpgradeModal';
import distributionApi from 'services/api/distribution';
import { useSubscription, useMessageSnackbar } from 'services/hooks';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import { type ReleaseData } from 'services/store/distribution/types';
import { isReleaseLimitExceededError } from 'services/helpers/error';
import { selectIsSubmitReleaseLimitReached } from 'services/store/user/selectors';
import { ReleaseInfo, ReleaseSuccessModal } from './components';
import { ButtonContainer, SubmitButton, GoBackButton } from './styled';

type PropsType = {
  release: ReleaseData;
  submitEnabled: boolean;
  onSubmitSuccess: () => void;
  onSubmitError: () => void;
};

const analyticsLocation = AnalyticsEventLocation.RELEASE_REVIEW_PAGE;

const SummaryInfo = ({
  release,
  submitEnabled,
  onSubmitSuccess,
  onSubmitError,
}: PropsType) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const [isReleaseLimitDialogOpen, setIsReleaseLimitDialogOpen] =
    useState(false);
  const isReleaseLimitReached = useSelector(selectIsSubmitReleaseLimitReached);
  const { displayErrorSnackbar } = useMessageSnackbar();

  const [submitRelease, { isLoading, isSuccess, error }] =
    distributionApi.useSubmitReleaseMutation();

  const { isFreeAccount } = useSubscription();

  const closeUpgradeModal = useCallback(() => {
    setIsUpgradeModalOpen(false);
  }, []);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    onSubmitSuccess();
  }, [isSuccess, onSubmitSuccess]);

  useEffect(() => {
    if (!error) {
      return;
    }
    if (isReleaseLimitExceededError(error)) {
      setIsUpsellModalOpen(true);
      return;
    }
    displayErrorSnackbar(t('messages:error:unknown'));
    onSubmitError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, error, onSubmitError]);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleReleaseLimitDialogClose = () => {
    setIsReleaseLimitDialogOpen(false);
  };

  const handleSubmitRelease = useCallback(() => {
    if (isFreeAccount) {
      setIsUpgradeModalOpen(true);
      return;
    }

    if (isReleaseLimitReached) {
      setIsReleaseLimitDialogOpen(true);
      return;
    }

    submitRelease({ releaseId: release.id });
  }, [release, submitRelease, isReleaseLimitReached, isFreeAccount]);

  return (
    <Box>
      <ReleaseInfo release={release} />
      <ButtonContainer>
        <GoBackButton onClick={handleGoBack}>
          {t('distribution:review:actions:goBack')}
        </GoBackButton>
        <SubmitButton
          disabled={!submitEnabled || isLoading}
          loading={isLoading}
          onClick={handleSubmitRelease}
        >
          {t('distribution:review:actions:submitForApproval')}
        </SubmitButton>
      </ButtonContainer>

      {isSuccess && <ReleaseSuccessModal />}
      <ReleaseLimitDialog
        isOpen={isReleaseLimitDialogOpen}
        onClose={handleReleaseLimitDialogClose}
      />
      <UpsellModal
        isOpen={isUpsellModalOpen}
        onClose={() => setIsUpsellModalOpen(false)}
        location={AnalyticsEventLocation.RELEASE_REVIEW_PAGE}
        releaseId={release.id}
      />
      <UpgradeDistributionModal
        isOpen={isUpgradeModalOpen}
        onClose={closeUpgradeModal}
        location={analyticsLocation}
      />
    </Box>
  );
};

export default SummaryInfo;
